'use client';
// AmanChain AI Agents — the agent-native marketplace (L0-L4).
// Agents register on-chain, publish services with fixed per-call prices, and
// every call is settled by a real agent_invoke transaction (AmanPay x402):
// caller pays the provider inside consensus. Node-operated services return
// real computed results; the whole economy loop closes on-chain.
import { useMemo, useState, useRef, useEffect } from 'react';
import {
  Bot, Zap, ShieldCheck, Radio, Loader2, Coins, Activity, BadgeCheck,
  Store, Wrench, CircleDollarSign, ArrowRight, BotOff, Sparkles, Clock3,
  Rocket, Repeat, Gauge, Download, Undo2, TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchJson, post, postX402, usePoll, fmtAmt, fmtInt, Mono, timeAgo, SectionHeader,
  StatCard, TypeBadge,
} from './shared';
import { useSession } from './session';
import { FirstAgentPay } from './first-agent-pay';
import { X402Receipts } from './x402-receipts';
import type { Net, AgentsData, ServiceViewT, X402Response, AutopilotStatusT } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const netQ = (net: Net) => `net=${net}`;

export function AgentMarket({ net }: { net: Net }) {
  const { session } = useSession();
  const { data, refresh } = usePoll<AgentsData>(
    async () => fetchJson(`/api/agents?${netQ(net)}${session ? `&address=${session.address}` : ''}`),
    4000, [net, session?.address],
  );
  const [invokeTarget, setInvokeTarget] = useState<ServiceViewT | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [filter, setFilter] = useState<string>('all');

  const services = useMemo(() => {
    const list = data?.services ?? [];
    return filter === 'all' ? list : list.filter(s => s.capability === filter);
  }, [data?.services, filter]);

  return (
    <div className="space-y-6">
      <SectionHeader icon={Bot} title="AI Agent Marketplace"
        desc="Autonomous agents register on-chain, publish paid services, and pay each other automatically per call — AmanPay x402 settles every invocation inside consensus."
        action={
          session ? (
            <Button onClick={() => setPublishOpen(true)} className="gap-2 font-black">
              <Store className="size-4" /> Register agent / publish service
            </Button>
          ) : undefined
        } />

      {/* The closed autonomous-economy loop */}
      <div className="glass p-4">
        <div className="flex flex-col md:flex-row items-stretch gap-3 text-xs">
          {[
            { t: '1 · Agent registers', d: 'agent_register — identity (KYA) + capabilities, signed by its owner wallet', i: BadgeCheck },
            { t: '2 · Service published', d: 'agent_service — fixed price per call enforced by consensus', i: Wrench },
            { t: '3 · Another agent calls', d: 'agent_invoke — the x402 payment moves AMAN to the provider in the block', i: Zap },
          ].map((s, idx) => (
            <div key={s.t} className="flex-1 flex items-start gap-2.5 rounded-xl bg-muted/40 ring-1 ring-border/60 p-3">
              <div className="rounded-lg bg-primary/10 text-primary ring-1 ring-primary/25 p-2 shrink-0"><s.i className="size-4" /></div>
              <div className="min-w-0">
                <div className="font-black text-[0.72rem]">{s.t}</div>
                <div className="text-muted-foreground mt-0.5 leading-relaxed">{s.d}</div>
              </div>
              {idx < 2 && <ArrowRight className="size-4 text-muted-foreground/50 hidden md:block self-center shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* First-agent x402 payment — real USDC on Base via the founder's own wallet */}
      <FirstAgentPay net={net} />

      {/* Real money received — settlements on both rails + the live on-chain vault balance (v2.15.2) */}
      <X402Receipts net={net} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Bot} label="Registered agents" accent="emerald" value={fmtInt(data?.stats.agents)} sub="On-chain KYA identities" />
        <StatCard icon={Store} label="Live services" accent="sky" value={fmtInt(data?.stats.activeServices)} sub={`${fmtInt(data?.stats.services)} published total`} />
        <StatCard icon={Zap} label="x402 paid calls" accent="gold" value={fmtInt(data?.stats.totalCalls)} sub="Settled in consensus" />
        <StatCard icon={CircleDollarSign} label="Agent economy volume" accent="violet" value={<>{fmtAmt(data?.stats.totalVolumeAman, 2)} <span className="text-xs text-muted-foreground">AMAN</span></>} sub="Paid agent-to-agent" />
      </div>

      {/* Auto-Pilot — the economy runs itself */}
      <AutopilotPanel net={net} services={data?.services ?? []} ap={data?.autopilot ?? null} />

      {/* Services marketplace */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <button onClick={() => setFilter('all')}
            className={`rounded-full px-3 py-1 text-[0.68rem] font-bold ring-1 transition ${filter === 'all' ? 'bg-primary/15 text-primary ring-primary/40' : 'ring-border text-muted-foreground hover:text-foreground'}`}>
            All services
          </button>
          {(data?.capabilities ?? []).map(c => (
            <button key={c} onClick={() => setFilter(c)}
              className={`rounded-full px-3 py-1 text-[0.68rem] font-bold ring-1 transition ${filter === c ? 'bg-primary/15 text-primary ring-primary/40' : 'ring-border text-muted-foreground hover:text-foreground'}`}>
              {c}
            </button>
          ))}
        </div>

        {(services.length ?? 0) === 0 ? (
          <div className="glass p-8 text-center text-sm text-muted-foreground">
            No services in this category yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {services.map(s => (
              <ServiceCard key={s.id} net={net} svc={s} onInvoke={() => setInvokeTarget(s)} />
            ))}
          </div>
        )}
      </div>

      {/* Recent x402 settlement tape */}
      <div className="glass p-4">
        <div className="flex items-center gap-2 text-sm font-bold mb-3"><Radio className="size-4 text-rose-400" /> Live x402 settlement tape</div>
        {(data?.history ?? []).length === 0 ? (
          <div className="text-xs text-muted-foreground">No agent calls yet — invoke a service to move the economy.</div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(data?.history ?? []).map(h => (
              <div key={h.txId} className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2 text-xs">
                <div className="min-w-0">
                  <div className="font-bold flex items-center gap-1.5">
                    <Zap className="size-3 text-amber-400" /> {h.serviceName}
                    {h.pending && <span className="text-amber-400">· pending</span>}
                  </div>
                  <Mono className="text-[0.62rem]">{h.caller.slice(0, 10)}… paid {h.provider.slice(0, 10)}…{h.height !== null ? ` · block #${fmtInt(h.height)}` : ''} · {timeAgo(h.ts)}</Mono>
                </div>
                <span className="font-black text-primary whitespace-nowrap" dir="ltr">{fmtAmt(h.amount)} AMAN</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My agents / services */}
      {session && data?.mine && (data.mine.agents.length > 0 || data.mine.services.length > 0) && (
        <div className="glass p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-bold"><Activity className="size-4 text-primary" /> My agent business</div>
          {data.mine.agents.map(a => (
            <div key={a.id} className="rounded-lg bg-muted/40 px-3 py-2 text-xs flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold">{a.name} <Mono className="ml-1">@{a.id}</Mono></div>
                <div className="text-muted-foreground">{a.model} · {a.services} service(s) · {fmtInt(a.calls)} calls</div>
              </div>
              <span className="font-black text-primary" dir="ltr">{fmtAmt(a.volumeAman, 2)} AMAN earned</span>
            </div>
          ))}
          {data.mine.services.filter(s => !s.nodeOperated).map(s => (
            <div key={s.id} className="rounded-lg bg-muted/40 px-3 py-2 text-xs flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-bold flex items-center gap-1.5">{s.name}
                  <TypeBadge type={s.active ? 'transfer' : 'unstake'} label={s.active ? 'ACTIVE' : 'PAUSED'} />
                </div>
                <div className="text-muted-foreground">{fmtAmt(s.priceAman)} AMAN/call · {fmtInt(s.calls)} calls · last {s.lastCalledAt ? timeAgo(s.lastCalledAt) : 'never'}</div>
              </div>
              <ToggleService net={net} svc={s} onDone={refresh} />
            </div>
          ))}
        </div>
      )}

      {invokeTarget && (
        <InvokeDialog net={net} svc={invokeTarget} onClose={() => setInvokeTarget(null)} onSettled={refresh} />
      )}
      {publishOpen && (
        <PublishDialog net={net} onClose={() => setPublishOpen(false)} onDone={() => { setPublishOpen(false); refresh(); }} />
      )}
    </div>
  );
}

function ServiceCard({ net, svc, onInvoke }: { net: Net; svc: ServiceViewT; onInvoke: () => void }) {
  const { session } = useSession();
  const isMine = session?.address === svc.owner;
  return (
    <div className="glass glass-hover p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="rounded-lg bg-violet-500/10 text-violet-400 ring-1 ring-violet-500/25 p-2">
          {svc.nodeOperated ? <ShieldCheck className="size-4" /> : <Bot className="size-4" />}
        </div>
        <span className="rounded-full bg-primary/10 text-primary ring-1 ring-primary/30 px-2 py-0.5 text-[0.62rem] font-black">{svc.capability}</span>
      </div>
      <div>
        <div className="font-black text-sm flex items-center gap-1.5">{svc.name}
          {!svc.active && <span className="text-amber-400 text-[0.62rem] font-bold">· PAUSED</span>}
        </div>
        <div className="text-[0.7rem] text-muted-foreground mt-1 leading-relaxed line-clamp-2">{svc.description}</div>
      </div>
      <div className="flex items-center gap-1.5 text-[0.65rem] text-muted-foreground">
        <BadgeCheck className="size-3 text-primary" />
        <span className="font-bold">{svc.agentName}</span>
        <Mono className="truncate">@{svc.agentId}</Mono>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-muted/50 py-1.5">
          <div className="text-[0.6rem] text-muted-foreground">Price / call</div>
          <div className="text-xs font-black text-primary" dir="ltr">{fmtAmt(svc.priceAman)}</div>
        </div>
        <div className="rounded-lg bg-muted/50 py-1.5">
          <div className="text-[0.6rem] text-muted-foreground">Calls</div>
          <div className="text-xs font-black" dir="ltr">{fmtInt(svc.calls)}</div>
        </div>
        <div className="rounded-lg bg-muted/50 py-1.5">
          <div className="text-[0.6rem] text-muted-foreground">Volume</div>
          <div className="text-xs font-black" dir="ltr">{fmtAmt(svc.volumeAman, 1)}</div>
        </div>
      </div>
      {isMine ? (
        <div className="text-[0.65rem] text-center text-muted-foreground">Your service — manage it below in “My agent business”</div>
      ) : (
        <Button onClick={onInvoke} disabled={!svc.active} className="w-full gap-2 font-black">
          <Zap className="size-4" /> Invoke — pay {fmtAmt(svc.priceAman)} AMAN (x402)
        </Button>
      )}
    </div>
  );
}

function ToggleService({ net, svc, onDone }: { net: Net; svc: ServiceViewT; onDone: () => void }) {
  const { session } = useSession();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password,
        type: 'agent_service', agentId: svc.agentId, serviceId: svc.id,
        active: !svc.active,
      });
      toast.success(`${svc.name} ${svc.active ? 'paused' : 'resumed'} — effective once mined`);
      setTimeout(onDone, 1500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy}
      className="rounded-lg ring-1 ring-border px-3 py-1.5 text-[0.68rem] font-bold hover:ring-primary/40 transition disabled:opacity-40">
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : svc.active ? 'Pause' : 'Resume'}
    </button>
  );
}

function InvokeDialog({ net, svc, onClose, onSettled }: {
  net: Net; svc: ServiceViewT; onClose: () => void; onSettled: () => void;
}) {
  const { session } = useSession();
  const [request, setRequest] = useState('');
  const [phase, setPhase] = useState<'form' | 'paying' | 'mining' | 'result'>('form');
  const [result, setResult] = useState<X402Response | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invoke = async () => {
    if (!session) return;
    setPhase('paying');
    setError(null);
    try {
      // Step 1 — the 402 quote (price/payTo enforced by the node, not the UI).
      // v2.19.2: MUST use the x402-aware poster — HTTP 402 IS the quote here
      // (the old `post` threw "Payment Required" on it and the dialog died
      // BEFORE any payment was attempted, for every service and balance).
      const quote = await postX402<X402Response>(`/api/x402?${netQ(net)}`, { serviceId: svc.id, request });
      if (!quote.requirements) throw new Error(quote.error ?? 'Service unavailable');
      setPhase('paying');
      // Step 2 — settle: agent_invoke moves the exact price to the provider in consensus
      const tx = await post<{ id: string }>(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password,
        type: 'agent_invoke', serviceId: svc.id, request,
      });
      setPhase('mining');
      // Step 3 — wait for the payment to be mined, then collect result + receipt.
      // v2.19.2: 120×1.5s = 180s window — image-gen legitimately runs up to
      // 145s on the node runtime (measured) + block time; the settle call
      // blocks until execution finishes and replays return instantly (cache).
      let settled: X402Response | null = null;
      for (let i = 0; i < 120; i++) {
        await new Promise(r => setTimeout(r, 1500));
        const attempt = await postX402<X402Response>(`/api/x402?${netQ(net)}`, {
          serviceId: svc.id, request, payment: { txId: tx.id },
        }).catch(() => null);
        if (attempt?.paid) { settled = attempt; break; }
      }
      if (!settled) throw new Error('Payment is taking longer than expected — check the Explorer; the call settles as soon as the block is mined.');
      setResult(settled);
      setPhase('result');
      onSettled();
      if (settled.executionFailed) {
        toast.warning(settled.refund
          ? `Execution failed — ${fmtAmt(settled.refund.amountAman)} AMAN was auto-refunded to your wallet`
          : 'Execution failed after settlement — see the dialog for details');
      } else {
        toast.success(`x402 payment settled — ${fmtAmt(settled.receipt?.amountAman ?? svc.priceAman)} AMAN paid to ${svc.agentName}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invocation failed');
      setPhase('form');
    }
  };

  // v2.20.0 — input contract from the catalog: a REQUIRED request disables
  // Pay until non-empty (live-proven failure: a founder paid 0.05 AMAN twice
  // for image-gen with an empty field and got guidance instead of an image).
  const requestRequired = svc.requestRequired
    ?? Boolean(svc.requestFormat && svc.requestFormat !== 'optional context');
  const requestPlaceholder = svc.id === 'aman-image-gen'
    ? 'A vivid image description, e.g. “A golden eagle flying over desert dunes at sunset”'
    : svc.requestFormat && svc.requestFormat !== 'optional context' ? svc.requestFormat
    : 'Anything the provider needs to know';

  // v2.19.2 — image services return a real 1024×1024 PNG as base64 (~1.4 MB):
  // render it as an actual IMAGE and keep the huge string out of the JSON dump.
  const rawResult = result?.result as { base64?: unknown; mime?: unknown; ok?: unknown; message?: unknown; hint?: unknown; error?: unknown } | null | undefined;
  const imageB64 = typeof rawResult?.base64 === 'string' && rawResult.base64.length > 128
    ? rawResult.base64 : null;
  const imageMime = typeof rawResult?.mime === 'string' ? rawResult.mime : 'image/png';
  const imageSrc = imageB64 ? `data:${imageMime};base64,${imageB64}` : null;
  const imageExt = imageMime === 'image/jpeg' ? 'jpg' : imageMime === 'image/webp' ? 'webp' : 'png';
  // v2.20.0 — paid-but-failed: show a FRIENDLY error box (never the raw
  // upstream JSON) plus the auto-refund banner when the node issued one.
  const resultFailed = Boolean(result?.executionFailed)
    || (Boolean(rawResult) && rawResult?.ok === false);
  const failMessage = typeof rawResult?.message === 'string' && rawResult.message
    ? rawResult.message
    : typeof rawResult?.error === 'string' && rawResult.error ? rawResult.error : null;
  const failHint = typeof rawResult?.hint === 'string' ? rawResult.hint : null;
  const resultJson = result?.result !== null && result?.result !== undefined && !resultFailed
    ? JSON.stringify(
        imageB64
          ? { ...rawResult, base64: `(${imageB64.length.toLocaleString('en-US')} base64 chars — preview shown above)` }
          : result.result,
        null, 2)
    : null;

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="glass sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="size-5 text-amber-400" /> Invoke “{svc.name}”</DialogTitle>
          <DialogDescription>
            AmanPay x402 — your wallet pays <b dir="ltr">{fmtAmt(svc.priceAman)} AMAN</b> to {svc.agentName} automatically, settled inside the next block.
          </DialogDescription>
        </DialogHeader>

        {phase === 'form' && (
          <div className="space-y-3">
            {svc.id === 'aman-token-audit' && (
              <div className="rounded-lg bg-sky-500/10 ring-1 ring-sky-500/30 px-3 py-2 text-[0.68rem] text-sky-300">
                Pass a token symbol in the request (e.g. <Mono className="text-sky-200">USDT</Mono>) — the auditor returns its live collateral backing, governance flags and pool depth.
              </div>
            )}
            {svc.id === 'aman-image-gen' && (
              <div className="rounded-lg bg-sky-500/10 ring-1 ring-sky-500/30 px-3 py-2 text-[0.68rem] text-sky-300">
                Describe the image you want — the generated 1024×1024 picture is shown here with a download button. If the provider’s content filter rejects the description, the payment is auto-refunded.
              </div>
            )}
            <div>
              <label className="text-xs font-bold mb-1.5 block">
                Request payload {requestRequired
                  ? <span className="text-amber-400">(required, ≤ 200 chars)</span>
                  : <span className="text-muted-foreground">(optional, ≤ 200 chars)</span>}
              </label>
              <Input value={request} onChange={e => setRequest(e.target.value.slice(0, 200))}
                placeholder={requestPlaceholder} />
              {requestRequired && (
                <div className="text-[0.65rem] text-muted-foreground mt-1.5">
                  Format: <Mono>{svc.requestFormat}</Mono> — the provider cannot execute this service without it.
                </div>
              )}
            </div>
            <div className="rounded-xl bg-muted/50 ring-1 ring-border p-3 space-y-1.5 text-[0.7rem]">
              <div className="flex justify-between"><span className="text-muted-foreground">Service price</span><b dir="ltr">{fmtAmt(svc.priceAman)} AMAN</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Network fee (50% burned)</span><b dir="ltr">0.001 AMAN</b></div>
              <div className="flex justify-between border-t border-border pt-1.5"><span className="text-muted-foreground">Pays to</span><Mono>{svc.owner.slice(0, 12)}…</Mono></div>
              <div className="flex justify-between border-t border-border pt-1.5 text-[0.65rem] text-muted-foreground">
                <span>Guarantee</span><span className="text-right">execution failed? the node auto-refunds the price</span>
              </div>
            </div>
            {error && <div className="rounded-lg bg-rose-500/10 ring-1 ring-rose-500/30 px-3 py-2 text-xs text-rose-400">{error}</div>}
            <Button onClick={invoke} disabled={requestRequired && !request.trim()} className="w-full gap-2 font-black">
              <Sparkles className="size-4" /> Pay &amp; execute (x402)
            </Button>
          </div>
        )}

        {(phase === 'paying' || phase === 'mining') && (
          <div className="space-y-3 py-4 text-center">
            <Loader2 className="size-7 animate-spin text-primary mx-auto" />
            <div className="text-sm font-black">{phase === 'paying' ? 'Signing the x402 payment…' : 'Waiting for the payment block…'}</div>
            <div className="text-xs text-muted-foreground">{phase === 'paying'
              ? 'The node enforces the exact price — the payment moves inside consensus.'
              : 'The provider is paid the moment the block is mined. This usually takes ~3 seconds.'}</div>
          </div>
        )}

        {phase === 'result' && result && (
          <div className="space-y-3">
            {resultFailed ? (
              <div className="rounded-lg bg-rose-500/10 ring-1 ring-rose-500/30 p-3 flex items-start gap-2 text-sm text-rose-400 font-bold">
                <TriangleAlert className="size-4 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div>Execution failed — nothing was delivered</div>
                  {failMessage && <div className="text-[0.72rem] font-normal text-rose-300/90 mt-1 leading-relaxed">{failMessage}</div>}
                  {failHint && <div className="text-[0.68rem] font-normal text-muted-foreground mt-1">{failHint}</div>}
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/30 p-3 flex items-center gap-2 text-sm text-emerald-400 font-bold">
                <BadgeCheck className="size-4" /> Settled — receipt #{(result.receipt?.txId ?? '').slice(0, 14)}…
              </div>
            )}
            {result.refund && (
              <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 p-3 flex items-start gap-2 text-sm text-amber-400 font-bold">
                <Undo2 className="size-4 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div>Auto-refunded — {fmtAmt(result.refund.amountAman)} AMAN returned to your wallet</div>
                  <div className="text-[0.68rem] font-normal text-amber-300/80 mt-0.5" dir="ltr">
                    refund tx #{result.refund.txId.slice(0, 18)}… — mined by the node within seconds
                  </div>
                </div>
              </div>
            )}
            {imageSrc && (
              <div className="rounded-xl ring-1 ring-border p-2 bg-background">
                <img src={imageSrc} alt="Generated service result" className="w-full rounded-lg" />
                <a
                  href={imageSrc}
                  download={`aman-${svc.id.replace(/^aman-/, '')}-${(result.receipt?.txId ?? 'image').slice(0, 12)}.${imageExt}`}
                  className="block"
                >
                  <Button className="w-full gap-2 font-black mt-2">
                    <Download className="size-4" /> Download image ({imageMime === 'image/jpeg' ? 'JPG' : imageMime === 'image/webp' ? 'WEBP' : 'PNG'} · 1024×1024)
                  </Button>
                </a>
              </div>
            )}
            {resultFailed && result.note && (
              <div className="rounded-lg bg-muted/50 p-3 text-[0.7rem] text-muted-foreground leading-relaxed">{result.note}</div>
            )}
            {resultJson && (
              <div className="rounded-xl bg-zinc-950 ring-1 ring-border p-3 overflow-x-auto">
                <pre className="text-[0.68rem] text-emerald-300/90 leading-relaxed" dir="ltr">{resultJson}</pre>
              </div>
            )}
            {!resultJson && !resultFailed && (
              <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">{result.note}</div>
            )}
            {result.executedByNode && !resultFailed && (
              <div className="text-[0.65rem] text-muted-foreground flex items-center gap-1.5">
                <ShieldCheck className="size-3.5 text-primary" /> {result.note}
              </div>
            )}
            <div className="flex gap-2">
              {resultFailed && (
                <Button onClick={() => { setResult(null); setPhase('form'); }} variant="secondary" className="flex-1 gap-2 font-black">
                  <Repeat className="size-4" /> Try again
                </Button>
              )}
              <Button onClick={onClose} variant={resultFailed ? 'ghost' : 'secondary'} className="flex-1 font-black">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PublishDialog({ net, onClose, onDone }: { net: Net; onClose: () => void; onDone: () => void }) {
  const { session } = useSession();
  const [tab, setTab] = useState<'agent' | 'service'>('agent');
  const [busy, setBusy] = useState(false);
  // agent form
  const [agentId, setAgentId] = useState('');
  const [agentName, setAgentName] = useState('');
  const [model, setModel] = useState('');
  const [caps, setCaps] = useState<string[]>([]);
  const [desc, setDesc] = useState('');
  // service form
  const [serviceAgentId, setServiceAgentId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [price, setPrice] = useState('0.1');
  const [capability, setCapability] = useState('pricing');
  const [serviceDesc, setServiceDesc] = useState('');

  const { data } = usePoll<AgentsData>(async () => fetchJson(`/api/agents?${netQ(net)}&address=${session?.address ?? ''}`), 8000, [net, session?.address]);
  const myAgentIds = (data?.mine?.agents ?? []).map(a => a.id);

  const submitAgent = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password,
        type: 'agent_register', agentId, name: agentName, model,
        capabilities: caps, description: desc,
      });
      toast.success('Agent registration submitted — mined within seconds');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Registration failed');
    } finally { setBusy(false); }
  };

  const submitService = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await post(`/api/tx?${netQ(net)}`, {
        walletId: session.walletId, password: session.password,
        type: 'agent_service', agentId: serviceAgentId, serviceId,
        name: serviceName, capability, priceAman: Number(price), description: serviceDesc,
      });
      toast.success('Service published — it goes live once the transaction is mined');
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Publication failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="glass sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Store className="size-5 text-primary" /> Join the agent economy</DialogTitle>
          <DialogDescription>
            Two on-chain steps: register your agent&apos;s identity, then publish a paid service. Both are ordinary signed transactions.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 rounded-xl ring-1 ring-input p-1">
          <button onClick={() => setTab('agent')} className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-bold transition ${tab === 'agent' ? 'bg-primary/15 text-primary ring-1 ring-primary/40' : 'text-muted-foreground'}`}>1 · Register agent</button>
          <button onClick={() => setTab('service')} className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-bold transition ${tab === 'service' ? 'bg-primary/15 text-primary ring-1 ring-primary/40' : 'text-muted-foreground'}`} disabled={myAgentIds.length === 0}>2 · Publish service</button>
        </div>

        {tab === 'agent' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-xs text-muted-foreground">Agent ID (lowercase, unique)</span>
                <Input value={agentId} onChange={e => setAgentId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32))} placeholder="my-trading-bot" className="mono" />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-muted-foreground">Display name</span>
                <Input value={agentName} onChange={e => setAgentName(e.target.value.slice(0, 40))} placeholder="My Trading Bot" />
              </label>
            </div>
            <label className="space-y-1 block">
              <span className="text-xs text-muted-foreground">Model / runtime (self-declared)</span>
              <Input value={model} onChange={e => setModel(e.target.value.slice(0, 60))} placeholder="glm-4.7 · python 3.12 runtime" />
            </label>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Capabilities (pick 1-8)</span>
              <div className="flex flex-wrap gap-1.5">
                {(data?.capabilities ?? []).map(c => (
                  <button key={c} type="button" onClick={() => setCaps(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c].slice(0, 8))}
                    className={`rounded-full px-2.5 py-1 text-[0.65rem] font-bold ring-1 transition ${caps.includes(c) ? 'bg-primary/15 text-primary ring-primary/40' : 'ring-border text-muted-foreground'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <label className="space-y-1 block">
              <span className="text-xs text-muted-foreground">What does your agent do?</span>
              <Input value={desc} onChange={e => setDesc(e.target.value.slice(0, 200))} placeholder="Executes mean-reversion strategies on AMAN pools" />
            </label>
            <Button onClick={submitAgent} disabled={busy || agentId.length < 3 || agentName.trim().length < 3 || model.trim().length < 2 || caps.length === 0} className="w-full gap-2 font-black">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <BadgeCheck className="size-4" />} Register agent on-chain
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="space-y-1 block">
              <span className="text-xs text-muted-foreground">Your agent</span>
              <select value={serviceAgentId} onChange={e => setServiceAgentId(e.target.value)}
                className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2.5 text-sm">
                <option value="">Select one of your agents…</option>
                {myAgentIds.map(id => <option key={id} value={id}>@{id}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-xs text-muted-foreground">Service ID (unique)</span>
                <Input value={serviceId} onChange={e => setServiceId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 64))} placeholder="price-alerts" className="mono" />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-muted-foreground">Service name</span>
                <Input value={serviceName} onChange={e => setServiceName(e.target.value.slice(0, 60))} placeholder="Price Alerts" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-xs text-muted-foreground">Price per call (AMAN)</span>
                <Input value={price} onChange={e => setPrice(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" className="mono" dir="ltr" />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-muted-foreground">Primary capability</span>
                <select value={capability} onChange={e => setCapability(e.target.value)}
                  className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2.5 text-sm">
                  {(data?.capabilities ?? []).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <label className="space-y-1 block">
              <span className="text-xs text-muted-foreground">What do callers get?</span>
              <Input value={serviceDesc} onChange={e => setServiceDesc(e.target.value.slice(0, 200))} placeholder="Sends a notification when AMAN crosses a target price" />
            </label>
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-[0.65rem] text-muted-foreground flex items-center gap-1.5">
              <Clock3 className="size-3.5" /> Every invocation pays this exact price to your wallet inside consensus — no chargebacks, no invoices.
            </div>
            <Button onClick={submitService} disabled={busy || !serviceAgentId || serviceId.length < 3 || serviceName.trim().length < 3 || !(Number(price) > 0) || serviceDesc.trim().length < 3} className="w-full gap-2 font-black">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Wrench className="size-4" />} Publish service
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Agent Auto-Pilot — the economy runs itself
//  • Node runner: the on-chain "aman-keeper" agent pays other
//    agents automatically (server scheduler + real x402 txs).
//  • Your wallet: an opt-in session loop that pays for whitelisted
//    service calls automatically within a hard daily budget.
// ============================================================

type APLog = { ts: number; serviceId: string; txId: string; amount: number; error?: string };

interface APConfig {
  enabled: boolean; maxCalls: number; budget: number; minIntervalMin: number;
  day: string; callsUsed: number; spent: number; lastRunAt: number; log: APLog[];
}

const AP_DEFAULT: APConfig = {
  enabled: false, maxCalls: 12, budget: 1, minIntervalMin: 3,
  day: '', callsUsed: 0, spent: 0, lastRunAt: 0, log: [],
};

function apKey(net: Net, address: string) { return `aman-ap-${net}-${address}`; }
function loadCfg(net: Net, address: string): APConfig {
  try {
    const raw = sessionStorage.getItem(apKey(net, address));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<APConfig>;
      return { ...AP_DEFAULT, ...parsed, log: Array.isArray(parsed.log) ? parsed.log.slice(0, 8) : [] };
    }
  } catch { /* corrupted config — reset */ }
  return { ...AP_DEFAULT, day: new Date().toISOString().slice(0, 10) };
}
function saveCfg(net: Net, address: string, cfg: APConfig) {
  try { sessionStorage.setItem(apKey(net, address), JSON.stringify({ ...cfg, log: cfg.log.slice(0, 8) })); } catch { /* storage full */ }
}
const todayKey = () => new Date().toISOString().slice(0, 10);

function AutopilotPanel({ net, services, ap }: { net: Net; services: ServiceViewT[]; ap: AutopilotStatusT | null }) {
  const { session } = useSession();
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 text-sm font-bold mb-3">
        <Rocket className="size-4 text-amber-400" /> Agent Auto-Pilot
        <span className="text-[0.62rem] font-bold text-muted-foreground hidden sm:inline">— the economy runs itself: an agent pays another agent automatically per call (AmanPay x402)</span>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <NodeRunnerCard ap={ap} />
        {session ? <UserAutopilot net={net} services={services} /> : (
          <div className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-4 text-xs text-muted-foreground flex items-center justify-center gap-2 text-center">
            <Repeat className="size-4 shrink-0" />
            Connect your wallet to run your own Auto-Pilot — your agents will pay other agents automatically, within a hard daily budget you control.
          </div>
        )}
      </div>
    </div>
  );
}

function NodeRunnerCard({ ap }: { ap: AutopilotStatusT | null }) {
  const live = ap?.registered ?? false;
  return (
    <div className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-black">
          <span className={`size-2 rounded-full ${live ? 'bg-emerald-400 pulse-dot' : 'bg-zinc-500'}`} />
          Node runner — @{ap?.agentId ?? 'aman-keeper'}
        </div>
        <TypeBadge type={live ? 'transfer' : 'unstake'} label={live ? 'RUNNING' : 'STARTING'} />
      </div>
      <p className="text-[0.68rem] text-muted-foreground leading-relaxed">
        Operated by this validator node: it invokes real services on schedule and settles every call
        with an on-chain x402 payment from its own wallet — visible in the settlement tape below.
      </p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-background/60 py-1.5">
          <div className="text-[0.58rem] text-muted-foreground">Auto runs</div>
          <div className="text-xs font-black" dir="ltr">{fmtInt(ap?.totalRuns)}</div>
        </div>
        <div className="rounded-lg bg-background/60 py-1.5">
          <div className="text-[0.58rem] text-muted-foreground">Paid today</div>
          <div className="text-xs font-black text-primary" dir="ltr">{fmtAmt(ap?.spentToday, 3)} / {fmtAmt(ap?.dailyCapAman, 0)}</div>
        </div>
        <div className="rounded-lg bg-background/60 py-1.5">
          <div className="text-[0.58rem] text-muted-foreground">Runner balance</div>
          <div className="text-xs font-black" dir="ltr">{fmtAmt(ap?.runner?.balance, 2)}</div>
        </div>
      </div>
      {(ap?.tasks ?? []).length > 0 && (
        <div className="space-y-1">
          {ap!.tasks.map(t => (
            <div key={t.serviceId} className="flex items-center justify-between rounded-lg bg-background/60 px-2.5 py-1.5 text-[0.64rem]">
              <Mono className="truncate">{t.serviceId}</Mono>
              <span className="text-muted-foreground whitespace-nowrap">every ~{t.intervalMin}m · {t.lastRunAt ? `last ${timeAgo(t.lastRunAt)}` : 'starting…'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UserAutopilot({ net, services }: { net: Net; services: ServiceViewT[] }) {
  const { session } = useSession();
  const address = session?.address ?? '';
  const [cfg, setCfg] = useState<APConfig>(() => address ? loadCfg(net, address) : { ...AP_DEFAULT });
  const [busy, setBusy] = useState(false);
  const cfgRef = useRef(cfg); cfgRef.current = cfg;
  const svcRef = useRef(services); svcRef.current = services;
  const inflight = useRef(false);
  const cursor = useRef(0);

  useEffect(() => {
    if (address) setCfg(loadCfg(net, address));
  }, [net, address]);

  const patch = (p: Partial<APConfig>) => {
    setCfg(prev => {
      const next = { ...prev, ...p };
      if (address) saveCfg(net, address, next);
      return next;
    });
  };

  const attempt = async () => {
    if (!session || inflight.current) return;
    const c = cfgRef.current;
    if (!c.enabled) return;
    // Day rollover resets the ledger
    const today = todayKey();
    const day = c.day === today ? c : { ...c, day: today, callsUsed: 0, spent: 0 };
    if (day !== c) patch({ day: today, callsUsed: 0, spent: 0 });
    if (day.callsUsed >= day.maxCalls || Date.now() - day.lastRunAt < day.minIntervalMin * 60_000) return;

    // Pick the next eligible service: active, not ours, affordable within the remaining budget
    const remaining = day.budget - day.spent;
    const list = svcRef.current.filter(s => s.active && s.owner !== address && s.priceAman <= remaining);
    if (list.length === 0) return;
    const svc = list[cursor.current % list.length];
    cursor.current = (cursor.current + 1) % list.length;

    inflight.current = true;
    setBusy(true);
    try {
      const tx = await post<{ id: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password,
        type: 'agent_invoke', serviceId: svc.id, request: 'Scheduled auto-pilot call',
      });
      patch({
        callsUsed: day.callsUsed + 1, spent: Math.round((day.spent + svc.priceAman) * 1e8) / 1e8,
        lastRunAt: Date.now(),
        log: [{ ts: Date.now(), serviceId: svc.name, txId: tx.id, amount: svc.priceAman }, ...day.log].slice(0, 8),
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'autopilot call failed';
      const fatal = /balance|insufficient|password|locked/i.test(msg);
      patch({
        lastRunAt: Date.now(),
        enabled: fatal ? false : day.enabled,
        log: [{ ts: Date.now(), serviceId: svc.name, txId: '', amount: 0, error: msg }, ...day.log].slice(0, 8),
      });
      if (fatal) toast.error(`Auto-Pilot stopped: ${msg}`);
    } finally {
      inflight.current = false;
      setBusy(false);
    }
  };

  // The loop: checks every 15 s; every settled call is a real signed x402 payment
  useEffect(() => {
    if (!cfg.enabled || !session) return;
    const t = setInterval(() => { void attempt(); }, 15_000);
    return () => clearInterval(t);
  }, [cfg.enabled, session?.address, net]);

  const remaining = Math.max(0, cfg.budget - cfg.spent);
  const eligible = services.filter(s => s.active && s.owner !== address && s.priceAman <= remaining).length;

  return (
    <div className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-black">
          <Gauge className="size-3.5 text-primary" /> Your wallet Auto-Pilot
        </div>
        <button
          onClick={() => patch({ enabled: !cfg.enabled, day: todayKey() })}
          disabled={busy}
          className={`rounded-lg px-3 py-1.5 text-[0.66rem] font-black ring-1 transition disabled:opacity-40 ${cfg.enabled
            ? 'bg-rose-500/10 text-rose-400 ring-rose-500/40 hover:bg-rose-500/20'
            : 'bg-primary/15 text-primary ring-primary/40 hover:bg-primary/25'}`}>
          {busy && cfg.enabled ? <Loader2 className="size-3.5 animate-spin inline mr-1" /> : null}
          {cfg.enabled ? 'Stop Auto-Pilot' : 'Start Auto-Pilot'}
        </button>
      </div>
      <p className="text-[0.68rem] text-muted-foreground leading-relaxed">
        While enabled, your wallet pays other agents automatically for service calls — every payment is a
        real signed x402 transaction inside consensus, capped by the limits below. It stops instantly when
        the budget is exhausted, the wallet locks, or you close the tab.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <label className="space-y-1">
          <span className="text-[0.6rem] text-muted-foreground">Max calls / day</span>
          <Input value={String(cfg.maxCalls)} inputMode="numeric"
            onChange={e => patch({ maxCalls: Math.min(50, Math.max(1, Number(e.target.value.replace(/[^0-9]/g, '')) || 1)) })}
            className="h-8 text-xs mono" dir="ltr" />
        </label>
        <label className="space-y-1">
          <span className="text-[0.6rem] text-muted-foreground">Budget / day (AMAN)</span>
          <Input value={String(cfg.budget)} inputMode="decimal"
            onChange={e => patch({ budget: Math.min(10, Math.max(0.01, Number(e.target.value.replace(/[^0-9.]/g, '')) || 0.01)) })}
            className="h-8 text-xs mono" dir="ltr" />
        </label>
        <label className="space-y-1">
          <span className="text-[0.6rem] text-muted-foreground">Min interval (min)</span>
          <Input value={String(cfg.minIntervalMin)} inputMode="numeric"
            onChange={e => patch({ minIntervalMin: Math.min(60, Math.max(1, Number(e.target.value.replace(/[^0-9]/g, '')) || 1)) })}
            className="h-8 text-xs mono" dir="ltr" />
        </label>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-background/60 py-1.5">
          <div className="text-[0.58rem] text-muted-foreground">Calls used</div>
          <div className="text-xs font-black" dir="ltr">{cfg.callsUsed} / {cfg.maxCalls}</div>
        </div>
        <div className="rounded-lg bg-background/60 py-1.5">
          <div className="text-[0.58rem] text-muted-foreground">Budget left</div>
          <div className="text-xs font-black text-primary" dir="ltr">{fmtAmt(remaining, 3)}</div>
        </div>
        <div className="rounded-lg bg-background/60 py-1.5">
          <div className="text-[0.58rem] text-muted-foreground">Eligible services</div>
          <div className="text-xs font-black" dir="ltr">{fmtInt(eligible)}</div>
        </div>
      </div>
      {cfg.log.length > 0 && (
        <div className="space-y-1 max-h-36 overflow-y-auto">
          {cfg.log.map((l, i) => (
            <div key={`${l.txId}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-background/60 px-2.5 py-1.5 text-[0.64rem]">
              <div className="min-w-0 flex items-center gap-1.5">
                {l.error ? <BotOff className="size-3 text-rose-400 shrink-0" /> : <Zap className="size-3 text-amber-400 shrink-0" />}
                <span className="font-bold truncate">{l.serviceId}</span>
                {l.error && <span className="text-rose-400 truncate">{l.error.slice(0, 48)}</span>}
              </div>
              <span className="text-muted-foreground whitespace-nowrap">{l.error ? 'failed' : `paid ${fmtAmt(l.amount)}`} · {timeAgo(l.ts)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
